import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/Scheduler.css";

const dayFormatter = new Intl.DateTimeFormat("pl-PL", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit"
});

const longDayFormatter = new Intl.DateTimeFormat("pl-PL", {
  weekday: "long",
  day: "numeric",
  month: "long"
});

const requestTypeLabels = {
  DAY_OFF: "Prośba o wolne",
  VACATION: "Urlop / nieobecność",
  AVAILABILITY: "Deklaracja dostępności",
  OPEN_SHIFT: "Zgłoszenie do otwartej zmiany",
  SHIFT_SWAP: "Propozycja zamiany zmiany"
};

const requestStatusLabels = {
  AWAITING_TARGET: "Czeka na decyzję drugiego pracownika",
  PENDING: "Oczekuje na kierownika",
  APPROVED: "Zatwierdzony",
  REJECTED: "Odrzucony",
  CANCELLED: "Anulowany"
};

const availabilityTypeLabels = {
  AVAILABLE: "Mogę pracować",
  PREFERRED: "Preferuję pracę"
};

const weekdayOptions = [
  ["MONDAY", "Poniedziałek"],
  ["TUESDAY", "Wtorek"],
  ["WEDNESDAY", "Środa"],
  ["THURSDAY", "Czwartek"],
  ["FRIDAY", "Piątek"],
  ["SATURDAY", "Sobota"],
  ["SUNDAY", "Niedziela"]
];

const weekdayLabels = Object.fromEntries(weekdayOptions);
const formatWeekdays = days => (days || []).map(day => weekdayLabels[day] || day).join(", ");

const parseLocalDate = value => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const dateToString = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const todayString = () => dateToString(new Date());

const startOfWeek = value => {
  const date = parseLocalDate(value);
  const weekday = date.getDay() || 7;
  date.setDate(date.getDate() - weekday + 1);
  return date;
};

const endOfWeek = value => {
  const date = startOfWeek(value);
  date.setDate(date.getDate() + 6);
  return date;
};

const startOfMonth = value => {
  const date = parseLocalDate(value);
  return new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0);
};

const endOfMonth = value => {
  const date = parseLocalDate(value);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0, 0);
};

const resolveRange = (view, anchor, customFrom, customTo) => {
  let from;
  let to;

  if (view === "DAY") {
    from = parseLocalDate(anchor);
    to = parseLocalDate(anchor);
  } else if (view === "WEEK") {
    from = startOfWeek(anchor);
    to = endOfWeek(anchor);
  } else if (view === "BIWEEK") {
    from = startOfWeek(anchor);
    to = new Date(from);
    to.setDate(to.getDate() + 13);
  } else if (view === "MONTH") {
    from = startOfMonth(anchor);
    to = endOfMonth(anchor);
  } else {
    from = parseLocalDate(customFrom);
    to = parseLocalDate(customTo);
  }

  if (!from) from = parseLocalDate(todayString());
  if (!to || to < from) to = new Date(from);
  return { dateFrom: dateToString(from), dateTo: dateToString(to) };
};

const shiftAnchor = (value, view, direction) => {
  const date = parseLocalDate(value);
  if (view === "MONTH") date.setMonth(date.getMonth() + direction);
  else if (view === "BIWEEK") date.setDate(date.getDate() + direction * 14);
  else if (view === "WEEK") date.setDate(date.getDate() + direction * 7);
  else date.setDate(date.getDate() + direction);
  return dateToString(date);
};

const buildDateRange = (dateFrom, dateTo) => {
  const from = parseLocalDate(dateFrom);
  const to = parseLocalDate(dateTo);
  const result = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    result.push({ key: dateToString(cursor), label: dayFormatter.format(cursor), longLabel: longDayFormatter.format(cursor) });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

const assignmentDate = value => value ? value.slice(0, 10) : "";
const formatTime = value => value ? value.slice(11, 16) : "";
const formatDateTime = value => value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "";

const shiftHours = shift => {
  if (!shift?.startsAt || !shift?.endsAt) return 0;
  return Math.max(0, (new Date(shift.endsAt).getTime() - new Date(shift.startsAt).getTime()) / 3600000);
};

const formatHours = value => `${Number(value || 0).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} h`;
const getErrorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const requestDateLabel = request => {
  if (request.dateFrom && request.dateTo) return request.dateFrom === request.dateTo ? request.dateFrom : `${request.dateFrom} – ${request.dateTo}`;
  const shift = request.targetShift || request.sourceShift;
  if (!shift?.startsAt) return "";
  return `${assignmentDate(shift.startsAt)} ${formatTime(shift.startsAt)}–${formatTime(shift.endsAt)}`;
};

const EmployeeWorkSchedule = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(true);
  const [calendarView, setCalendarView] = useState("WEEK");
  const [calendarAnchor, setCalendarAnchor] = useState(todayString());
  const [customDateFrom, setCustomDateFrom] = useState(todayString());
  const [customDateTo, setCustomDateTo] = useState(todayString());
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [incomingSwaps, setIncomingSwaps] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [requestPolicy, setRequestPolicy] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapSource, setSwapSource] = useState(null);
  const [swapCandidates, setSwapCandidates] = useState([]);
  const [selectedSwapTarget, setSelectedSwapTarget] = useState("");
  const [swapLoading, setSwapLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requestForm, setRequestForm] = useState({
    type: "DAY_OFF",
    dateFrom: todayString(),
    dateTo: todayString(),
    absenceTypeId: "",
    availabilityType: "AVAILABLE",
    availabilityTimeFrom: "",
    availabilityTimeTo: "",
    availabilityRecurrence: "NONE",
    availabilityDaysOfWeek: [],
    employeeComment: ""
  });

  const visibleRange = useMemo(
    () => resolveRange(calendarView, calendarAnchor, customDateFrom, customDateTo),
    [calendarView, calendarAnchor, customDateFrom, customDateTo]
  );

  const days = useMemo(
    () => buildDateRange(visibleRange.dateFrom, visibleRange.dateTo),
    [visibleRange.dateFrom, visibleRange.dateTo]
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      const [scheduleResponse, requestsResponse, openShiftsResponse, incomingSwapsResponse, policyResponse] = await Promise.all([
        axiosPrivate.get("/employee-work-schedule", { params: visibleRange }),
        axiosPrivate.get("/schedule-change-requests/mine"),
        axiosPrivate.get("/schedule-change-requests/open-shifts", { params: visibleRange }),
        axiosPrivate.get("/schedule-change-requests/mine/incoming-swaps"),
        axiosPrivate.get("/schedule-change-requests/policy")
      ]);
      setData(scheduleResponse.data);
      setRequests(requestsResponse.data || []);
      setOpenShifts(openShiftsResponse.data || []);
      setIncomingSwaps(incomingSwapsResponse.data || []);
      setRequestPolicy(policyResponse.data || null);
    } catch (error) {
      setData(null);
      setRequests([]);
      setOpenShifts([]);
      setIncomingSwaps([]);
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [axiosPrivate, visibleRange.dateFrom, visibleRange.dateTo]);

  useEffect(() => {
    const loadAbsenceTypes = async () => {
      try {
        const response = await axiosPrivate.get("/absence-types");
        setAbsenceTypes(response.data || []);
      } catch (error) {
        setAbsenceTypes([]);
      }
    };
    loadAbsenceTypes();
  }, [axiosPrivate]);

  const shiftsByDate = useMemo(() => {
    const result = new Map();
    (data?.shifts ?? []).forEach(shift => {
      const date = assignmentDate(shift.startsAt);
      if (!result.has(date)) result.set(date, []);
      result.get(date).push(shift);
    });
    return result;
  }, [data?.shifts]);

  const requestsByDate = useMemo(() => {
    const result = new Map();
    requests.filter(request => ["DAY_OFF", "VACATION"].includes(request.type) && request.status !== "CANCELLED").forEach(request => {
      const from = parseLocalDate(request.dateFrom);
      const to = parseLocalDate(request.dateTo);
      if (!from || !to) return;
      const cursor = new Date(from);
      while (cursor <= to) {
        const key = dateToString(cursor);
        if (!result.has(key)) result.set(key, []);
        result.get(key).push(request);
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return result;
  }, [requests]);

  const summary = useMemo(() => {
    const shifts = data?.shifts ?? [];
    const hours = shifts.reduce((sum, shift) => sum + shiftHours(shift), 0);
    const workingDays = new Set(shifts.map(shift => assignmentDate(shift.startsAt))).size;
    return { shifts: shifts.length, hours, workingDays };
  }, [data?.shifts]);

  const resetRequestForm = () => {
    setRequestForm({
      type: "DAY_OFF",
      dateFrom: todayString(),
      dateTo: todayString(),
      absenceTypeId: "",
      availabilityType: "AVAILABLE",
      availabilityTimeFrom: "",
      availabilityTimeTo: "",
      availabilityRecurrence: "NONE",
      availabilityDaysOfWeek: [],
      employeeComment: ""
    });
  };

  const toggleAvailabilityWeekday = day => {
    setRequestForm(prev => ({
      ...prev,
      availabilityDaysOfWeek: prev.availabilityDaysOfWeek.includes(day)
        ? prev.availabilityDaysOfWeek.filter(item => item !== day)
        : [...prev.availabilityDaysOfWeek, day]
    }));
  };

  const submitDateRequest = async () => {
    if (!requestForm.dateFrom || !requestForm.dateTo || requestForm.dateTo < requestForm.dateFrom) {
      toastError("Podaj prawidłowy okres wniosku.");
      return;
    }
    if (requestForm.type === "VACATION" && !requestForm.absenceTypeId) {
      toastError("Wybierz typ nieobecności.");
      return;
    }
    if (requestForm.type === "AVAILABILITY") {
      const oneTimeMissing = Boolean(requestForm.availabilityTimeFrom) !== Boolean(requestForm.availabilityTimeTo);
      if (oneTimeMissing) {
        toastError("Podaj obie godziny dostępności albo pozostaw obie puste.");
        return;
      }
      if (requestForm.availabilityTimeFrom && requestForm.availabilityTimeTo && requestForm.availabilityTimeFrom >= requestForm.availabilityTimeTo) {
        toastError("Godzina zakończenia musi być późniejsza niż rozpoczęcia.");
        return;
      }
      if (requestForm.availabilityRecurrence === "WEEKLY" && requestForm.availabilityDaysOfWeek.length === 0) {
        toastError("Wybierz co najmniej jeden dzień tygodnia.");
        return;
      }
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/schedule-change-requests/mine", {
        type: requestForm.type,
        dateFrom: requestForm.dateFrom,
        dateTo: requestForm.dateTo,
        absenceTypeId: requestForm.type === "VACATION" ? Number(requestForm.absenceTypeId) : null,
        availabilityType: requestForm.type === "AVAILABILITY" ? requestForm.availabilityType : null,
        availabilityTimeFrom: requestForm.type === "AVAILABILITY" && requestForm.availabilityTimeFrom ? requestForm.availabilityTimeFrom : null,
        availabilityTimeTo: requestForm.type === "AVAILABILITY" && requestForm.availabilityTimeTo ? requestForm.availabilityTimeTo : null,
        availabilityRecurrence: requestForm.type === "AVAILABILITY" ? requestForm.availabilityRecurrence : null,
        availabilityDaysOfWeek: requestForm.type === "AVAILABILITY" ? requestForm.availabilityDaysOfWeek : [],
        employeeComment: requestForm.employeeComment
      });
      toastSuccess("Wniosek został zapisany.");
      setShowRequestModal(false);
      resetRequestForm();
      await loadAll();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const requestOpenShift = async shift => {
    setSaving(true);
    try {
      await axiosPrivate.post("/schedule-change-requests/mine", {
        type: "OPEN_SHIFT",
        targetAssignmentId: shift.assignmentId
      });
      toastSuccess("Zgłoszenie do otwartej zmiany zostało wysłane.");
      await loadAll();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openSwap = async shift => {
    setSwapSource(shift);
    setSelectedSwapTarget("");
    setSwapCandidates([]);
    setShowSwapModal(true);
    setSwapLoading(true);
    try {
      const response = await axiosPrivate.get("/schedule-change-requests/swap-candidates", {
        params: {
          sourceAssignmentId: shift.assignmentId,
          ...visibleRange
        }
      });
      setSwapCandidates(response.data || []);
    } catch (error) {
      toastError(getErrorMessage(error));
      setShowSwapModal(false);
    } finally {
      setSwapLoading(false);
    }
  };

  const submitSwap = async () => {
    if (!swapSource || !selectedSwapTarget) {
      toastError("Wybierz zmianę, z którą chcesz się zamienić.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/schedule-change-requests/mine", {
        type: "SHIFT_SWAP",
        sourceAssignmentId: swapSource.assignmentId,
        targetAssignmentId: Number(selectedSwapTarget)
      });
      toastSuccess("Propozycja zamiany została wysłana.");
      setShowSwapModal(false);
      await loadAll();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const cancelRequest = async request => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/schedule-change-requests/mine/${request.id}`);
      toastSuccess("Wniosek został anulowany.");
      await loadAll();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const decideIncomingSwap = async (request, decision) => {
    setSaving(true);
    try {
      await axiosPrivate.post(`/schedule-change-requests/mine/incoming-swaps/${request.id}/${decision}`);
      toastSuccess(decision === "accept"
        ? "Zaakceptowano propozycję. Wniosek oczekuje teraz na kierownika."
        : "Odrzucono propozycję zamiany.");
      await loadAll();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const hasPendingOpenShiftRequest = assignmentId => requests.some(request =>
    request.type === "OPEN_SHIFT" && request.status === "PENDING" && request.targetShift?.assignmentId === assignmentId
  );

  const hasPendingSwapRequest = assignmentId => requests.some(request =>
    request.type === "SHIFT_SWAP"
      && ["AWAITING_TARGET", "PENDING"].includes(request.status)
      && request.sourceShift?.assignmentId === assignmentId
  );

  const requestMinDate = requestForm.type === "DAY_OFF"
    ? requestPolicy?.earliestDayOffDate || todayString()
    : requestForm.type === "VACATION"
      ? requestPolicy?.earliestVacationDate || todayString()
      : todayString();
  const requestAdvanceDays = requestForm.type === "DAY_OFF"
    ? requestPolicy?.dayOffMinimumAdvanceDays
    : requestForm.type === "VACATION" ? requestPolicy?.vacationMinimumAdvanceDays : null;

  return (
    <div id="layoutSidenav_content">
      <ToastContainer />
      <div className="app-component-card-header shadow my-schedule-header">
        <div>
          <div className="my-schedule-title">Mój grafik</div>
          <div className="my-schedule-subtitle">Opublikowane zmiany, prośby do grafiku, urlopy, otwarte zmiany i propozycje zamiany.</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {data?.employeeFullName && <div className="my-schedule-employee">{data.employeeFullName}</div>}
          <Button size="sm" onClick={() => setShowRequestModal(true)}>Nowy wniosek</Button>
        </div>
      </div>

      <div className="container-fluid px-4">
        <div className="schedule-section my-schedule-section">
          <div className="schedule-calendar-toolbar my-schedule-toolbar">
            <div className="schedule-calendar-toolbar-nav">
              <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(shiftAnchor(calendarAnchor, calendarView, -1))} disabled={calendarView === "CUSTOM"}>‹</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(todayString())} disabled={calendarView === "CUSTOM"}>Dzisiaj</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(shiftAnchor(calendarAnchor, calendarView, 1))} disabled={calendarView === "CUSTOM"}>›</Button>
            </div>

            <Form.Select value={calendarView} onChange={event => setCalendarView(event.target.value)}>
              <option value="DAY">Dzień</option>
              <option value="WEEK">Tydzień</option>
              <option value="BIWEEK">2 tygodnie</option>
              <option value="MONTH">Miesiąc</option>
              <option value="CUSTOM">Dowolny zakres</option>
            </Form.Select>

            {calendarView === "CUSTOM" ? (
              <div className="schedule-calendar-custom-range">
                <Form.Control type="date" value={customDateFrom} onChange={event => setCustomDateFrom(event.target.value)} />
                <span>–</span>
                <Form.Control type="date" min={customDateFrom} value={customDateTo} onChange={event => setCustomDateTo(event.target.value)} />
              </div>
            ) : (
              <Form.Control type="date" value={calendarAnchor} onChange={event => setCalendarAnchor(event.target.value)} />
            )}

            <span className="schedule-calendar-visible-range">{visibleRange.dateFrom} – {visibleRange.dateTo}</span>
          </div>

          <div className="my-schedule-summary">
            <div><span>Zmiany</span><strong>{summary.shifts}</strong></div>
            <div><span>Dni pracy</span><strong>{summary.workingDays}</strong></div>
            <div><span>Godziny</span><strong>{formatHours(summary.hours)}</strong></div>
            <div><span>Wnioski oczekujące</span><strong>{requests.filter(item => ["AWAITING_TARGET", "PENDING"].includes(item.status)).length}</strong></div>
            <div><span>Otwarte zmiany</span><strong>{openShifts.length}</strong></div>
            <div><span>Zamiany do decyzji</span><strong>{incomingSwaps.length}</strong></div>
          </div>

          {loading ? <LoadingDataDiv /> : (
            <div className="my-schedule-grid">
              {days.map(day => {
                const shifts = shiftsByDate.get(day.key) ?? [];
                const dayRequests = requestsByDate.get(day.key) ?? [];
                const dailyHours = shifts.reduce((sum, shift) => sum + shiftHours(shift), 0);
                const today = day.key === todayString();

                return (
                  <section key={day.key} className={`my-schedule-day ${today ? "my-schedule-day-today" : ""}`}>
                    <header>
                      <div>{day.longLabel}</div>
                      <span>{dailyHours > 0 ? formatHours(dailyHours) : "Wolne"}</span>
                    </header>

                    <div className="my-schedule-day-body">
                      {dayRequests.map(request => (
                        <div key={`request-${request.id}`} className={`my-schedule-request-badge my-schedule-request-${String(request.status).toLowerCase()}`}>
                          {requestTypeLabels[request.type]} · {requestStatusLabels[request.status]}
                        </div>
                      ))}

                      {shifts.length === 0 ? (
                        <div className="my-schedule-free-day">Brak zaplanowanej pracy</div>
                      ) : shifts.map(shift => (
                        <article key={shift.assignmentId} className="my-schedule-shift">
                          <div className="my-schedule-shift-time">{formatTime(shift.startsAt)}–{formatTime(shift.endsAt)}</div>
                          <div className="my-schedule-shift-position">{shift.positionName || "Stanowisko nieokreślone"}</div>
                          {shift.locationName && <div className="my-schedule-shift-location">📍 {shift.locationName}</div>}
                          <div className="my-schedule-shift-meta">
                            <span>{shift.departmentName}</span>
                            <span>{shift.scheduleName}</span>
                          </div>
                          {shift.notes && <div className="my-schedule-shift-notes">{shift.notes}</div>}
                          <div className="my-schedule-shift-actions">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => openSwap(shift)}
                              disabled={saving || hasPendingSwapRequest(shift.assignmentId)}
                            >
                              {hasPendingSwapRequest(shift.assignmentId) ? "Zamiana oczekuje" : "Zaproponuj zamianę"}
                            </Button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>

        <div className="schedule-section my-schedule-section">
          <div className="schedule-section-header">
            <div>
              <h6>Propozycje zamiany dla mnie</h6>
              <span>Zmiana trafi do kierownika dopiero po Twojej akceptacji.</span>
            </div>
          </div>
          {incomingSwaps.length === 0 ? (
            <div className="my-schedule-empty">Nie masz propozycji zamiany oczekujących na Twoją decyzję.</div>
          ) : (
            <div className="schedule-request-list">
              {incomingSwaps.map(request => (
                <div key={request.id} className="schedule-request-card">
                  <div className="schedule-request-card-main">
                    <div className="schedule-request-card-title">
                      <strong>{request.employeeFullName} proponuje zamianę</strong>
                      <span className="schedule-request-status schedule-request-status-awaiting_target">Czeka na Twoją decyzję</span>
                    </div>
                    {request.sourceShift && request.targetShift && (
                      <div className="schedule-request-swap-summary">
                        Otrzymasz: {assignmentDate(request.sourceShift.startsAt)} {formatTime(request.sourceShift.startsAt)}–{formatTime(request.sourceShift.endsAt)} · {request.sourceShift.positionName || "Brak stanowiska"}{request.sourceShift.locationName ? ` · ${request.sourceShift.locationName}` : ""}<br />
                        Oddasz: {assignmentDate(request.targetShift.startsAt)} {formatTime(request.targetShift.startsAt)}–{formatTime(request.targetShift.endsAt)} · {request.targetShift.positionName || "Brak stanowiska"}{request.targetShift.locationName ? ` · ${request.targetShift.locationName}` : ""}
                      </div>
                    )}
                    {request.employeeComment && <div className="schedule-request-comment"><strong>Uwagi:</strong> {request.employeeComment}</div>}
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="success" onClick={() => decideIncomingSwap(request, "accept")} disabled={saving}>Akceptuję</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => decideIncomingSwap(request, "reject")} disabled={saving}>Odrzucam</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="schedule-section my-schedule-section">
          <div className="schedule-section-header">
            <div>
              <h6>Otwarte zmiany</h6>
              <span>Pokazywane są tylko zmiany, do których aktualnie spełniasz warunki przydziału.</span>
            </div>
          </div>
          {openShifts.length === 0 ? (
            <div className="my-schedule-empty">Brak dostępnych otwartych zmian w wybranym okresie.</div>
          ) : (
            <div className="schedule-request-shift-list">
              {openShifts.map(shift => (
                <div key={shift.assignmentId} className="schedule-request-shift-card">
                  <div>
                    <strong>{assignmentDate(shift.startsAt)} · {formatTime(shift.startsAt)}–{formatTime(shift.endsAt)}</strong>
                    <span>{shift.positionName} · {shift.departmentName}{shift.locationName ? ` · ${shift.locationName}` : ""}</span>
                    {shift.warnings?.length > 0 && <small>{shift.warnings.join("; ")}</small>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => requestOpenShift(shift)}
                    disabled={saving || hasPendingOpenShiftRequest(shift.assignmentId)}
                  >
                    {hasPendingOpenShiftRequest(shift.assignmentId) ? "Zgłoszenie oczekuje" : "Zgłoś chęć"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="schedule-section my-schedule-section">
          <div className="schedule-section-header">
            <div>
              <h6>Moje wnioski</h6>
              <span>Historia próśb, urlopów, deklaracji dostępności, zgłoszeń do otwartych zmian i propozycji zamiany.</span>
            </div>
          </div>
          {requests.length === 0 ? (
            <div className="my-schedule-empty">Nie masz jeszcze żadnych wniosków.</div>
          ) : (
            <div className="schedule-request-list">
              {requests.map(request => (
                <div key={request.id} className="schedule-request-card">
                  <div className="schedule-request-card-main">
                    <div className="schedule-request-card-title">
                      <strong>{requestTypeLabels[request.type] || request.type}</strong>
                      <span className={`schedule-request-status schedule-request-status-${String(request.status).toLowerCase()}`}>{requestStatusLabels[request.status] || request.status}</span>
                    </div>
                    <div className="schedule-request-card-meta">
                      <span>{requestDateLabel(request)}</span>
                      <span>{request.departmentName}</span>
                      <span>Złożono: {formatDateTime(request.createdAt)}</span>
                    </div>
                    {request.type === "SHIFT_SWAP" && request.sourceShift && request.targetShift && (
                      <div className="schedule-request-swap-summary">
                        Twoja: {assignmentDate(request.sourceShift.startsAt)} {formatTime(request.sourceShift.startsAt)}–{formatTime(request.sourceShift.endsAt)} → {request.targetShift.employeeFullName}: {assignmentDate(request.targetShift.startsAt)} {formatTime(request.targetShift.startsAt)}–{formatTime(request.targetShift.endsAt)}
                      </div>
                    )}
                    {request.type === "AVAILABILITY" && (
                      <div className="schedule-request-availability-summary">
                        <strong>{availabilityTypeLabels[request.availabilityType] || request.availabilityType}</strong>
                        {request.availabilityTimeFrom && request.availabilityTimeTo && <span> · {String(request.availabilityTimeFrom).slice(0, 5)}–{String(request.availabilityTimeTo).slice(0, 5)}</span>}
                        {request.availabilityRecurrence === "WEEKLY" && request.availabilityDaysOfWeek?.length > 0 && <span> · {formatWeekdays(request.availabilityDaysOfWeek)}</span>}
                      </div>
                    )}
                    {request.employeeComment && <div className="schedule-request-comment">{request.employeeComment}</div>}
                    {request.managerComment && <div className="schedule-request-manager-comment"><strong>Odpowiedź:</strong> {request.managerComment}</div>}
                  </div>
                  {["AWAITING_TARGET", "PENDING"].includes(request.status) && (
                    <Button size="sm" variant="outline-danger" onClick={() => cancelRequest(request)} disabled={saving}>Anuluj</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nowy wniosek do grafiku</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Rodzaj</Form.Label>
            <Form.Select value={requestForm.type} onChange={event => setRequestForm(prev => ({ ...prev, type: event.target.value }))}>
              <option value="DAY_OFF">Prośba o wolne</option>
              <option value="VACATION">Urlop / nieobecność</option>
              <option value="AVAILABILITY">Deklaracja dostępności / gotowości do pracy</option>
            </Form.Select>
          </Form.Group>
          {["DAY_OFF", "VACATION"].includes(requestForm.type) && requestAdvanceDays != null && (
            <Alert variant="info" className="py-2">
              Ten typ wniosku wymaga wyprzedzenia <strong>{requestAdvanceDays} dni</strong>. Najwcześniejszy dopuszczalny termin: <strong>{requestMinDate}</strong>.
            </Alert>
          )}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <Form.Label>Od</Form.Label>
              <Form.Control type="date" min={requestMinDate} value={requestForm.dateFrom} onChange={event => setRequestForm(prev => ({ ...prev, dateFrom: event.target.value, dateTo: prev.dateTo < event.target.value ? event.target.value : prev.dateTo }))} />
            </div>
            <div className="col-6">
              <Form.Label>Do</Form.Label>
              <Form.Control type="date" min={requestForm.dateFrom} value={requestForm.dateTo} onChange={event => setRequestForm(prev => ({ ...prev, dateTo: event.target.value }))} />
            </div>
          </div>
          {requestForm.type === "VACATION" && (
            <Form.Group className="mb-3">
              <Form.Label>Typ nieobecności</Form.Label>
              <Form.Select value={requestForm.absenceTypeId} onChange={event => setRequestForm(prev => ({ ...prev, absenceTypeId: event.target.value }))}>
                <option value="">-- wybierz --</option>
                {absenceTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
              </Form.Select>
            </Form.Group>
          )}
          {requestForm.type === "AVAILABILITY" && (
            <div className="schedule-availability-request-box mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Rodzaj deklaracji</Form.Label>
                <Form.Select value={requestForm.availabilityType} onChange={event => setRequestForm(prev => ({ ...prev, availabilityType: event.target.value }))}>
                  <option value="AVAILABLE">Mogę pracować</option>
                  <option value="PREFERRED">Preferuję pracę</option>
                </Form.Select>
                <Form.Text className="text-muted">Niedostępność zgłaszaj jako prośbę o wolne albo urlop, ponieważ wymaga osobnej decyzji kierownika.</Form.Text>
              </Form.Group>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Form.Label>Godzina od</Form.Label>
                  <Form.Control type="time" value={requestForm.availabilityTimeFrom} onChange={event => setRequestForm(prev => ({ ...prev, availabilityTimeFrom: event.target.value }))} />
                </div>
                <div className="col-6">
                  <Form.Label>Godzina do</Form.Label>
                  <Form.Control type="time" value={requestForm.availabilityTimeTo} onChange={event => setRequestForm(prev => ({ ...prev, availabilityTimeTo: event.target.value }))} />
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Powtarzanie</Form.Label>
                <Form.Select value={requestForm.availabilityRecurrence} onChange={event => setRequestForm(prev => ({ ...prev, availabilityRecurrence: event.target.value, availabilityDaysOfWeek: event.target.value === "WEEKLY" ? prev.availabilityDaysOfWeek : [] }))}>
                  <option value="NONE">Każdy dzień wybranego okresu</option>
                  <option value="WEEKLY">Tylko wybrane dni tygodnia</option>
                </Form.Select>
              </Form.Group>
              {requestForm.availabilityRecurrence === "WEEKLY" && (
                <div className="schedule-availability-weekdays">
                  {weekdayOptions.map(([day, label]) => (
                    <label key={day} className={`schedule-availability-weekday ${requestForm.availabilityDaysOfWeek.includes(day) ? "selected" : ""}`}>
                      <Form.Check type="checkbox" checked={requestForm.availabilityDaysOfWeek.includes(day)} onChange={() => toggleAvailabilityWeekday(day)} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <Form.Group>
            <Form.Label>Uwagi</Form.Label>
            <Form.Control as="textarea" rows={3} value={requestForm.employeeComment} onChange={event => setRequestForm(prev => ({ ...prev, employeeComment: event.target.value }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRequestModal(false)}>Anuluj</Button>
          <Button onClick={submitDateRequest} disabled={saving}>{saving ? "Zapisywanie..." : "Wyślij wniosek"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSwapModal} onHide={() => setShowSwapModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Propozycja zamiany zmiany</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {swapSource && (
            <div className="schedule-request-own-shift mb-3">
              <strong>Twoja zmiana:</strong> {assignmentDate(swapSource.startsAt)} {formatTime(swapSource.startsAt)}–{formatTime(swapSource.endsAt)} · {swapSource.positionName}{swapSource.locationName ? ` · ${swapSource.locationName}` : ""}
            </div>
          )}
          {swapLoading ? <LoadingDataDiv /> : swapCandidates.length === 0 ? (
            <div className="my-schedule-empty">Nie znaleziono zmian, które można bezpiecznie zamienić z Twoją zmianą.</div>
          ) : (
            <div className="schedule-request-swap-candidates">
              {swapCandidates.map(candidate => (
                <label key={candidate.assignmentId} className={`schedule-request-swap-candidate ${String(selectedSwapTarget) === String(candidate.assignmentId) ? "selected" : ""}`}>
                  <Form.Check
                    type="radio"
                    name="swapTarget"
                    value={candidate.assignmentId}
                    checked={String(selectedSwapTarget) === String(candidate.assignmentId)}
                    onChange={event => setSelectedSwapTarget(event.target.value)}
                  />
                  <div>
                    <strong>{assignmentDate(candidate.startsAt)} · {formatTime(candidate.startsAt)}–{formatTime(candidate.endsAt)}</strong>
                    <span>{candidate.employeeFullName} · {candidate.positionName}{candidate.locationName ? ` · ${candidate.locationName}` : ""}</span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSwapModal(false)}>Anuluj</Button>
          <Button onClick={submitSwap} disabled={saving || swapLoading || !selectedSwapTarget}>Wyślij propozycję</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeWorkSchedule;

